/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { When } from "react-if";
import { theme, Text } from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { isNotEmpty } from "~/tools";
import { KeyValueWithColor, KpiTimePoint, TARGET_TYPE } from "../types";
import { getKpiValueColor } from "../utils";

type Props = KpiTimePoint & {
  targetType: TARGET_TYPE;
  kpiName: string;
  additionalTitle?: string;
  disabledFields?: string[];
};

export function TimePointTooltip({
  date,
  value,
  additionalValue,
  targetName,
  isEstimated,
  targetValue,
  additionalFields,
  targetType,
  disabledFields = [],
}: Props) {
  const valueSuffix = isEstimated ? "*" : "";

  return (
    <>
      <Text size="sm" mb="0" color={theme.color.text.text04}>
        {dayjs(date).format(dateFormat.dayShortMonthYear)}
      </Text>
      <TimePointDetailsTable
        values={[
          {
            key: "KPI Value [%]",
            value: value != null ? `${value}${valueSuffix}` : "-",
            color: getKpiValueColor({
              kpiValue: value,
              targetValue,
              targetType,
            }),
          },
          ...(additionalValue
            ? [
                {
                  key: "Organizational Unit Value [%]",
                  value: additionalValue.toString(),
                },
              ]
            : []),
          {
            key: "Target Value [%]",
            value: targetValue != null ? targetValue.toString() : "Unspecified",
          },
          ...(targetName
            ? [
                {
                  key: "Target Name",
                  value: targetName,
                },
              ]
            : []),
          ...(additionalFields
            ?.filter((field) => isNotEmpty(field.key))
            .filter((field) => disabledFields?.includes(field.key) === false) ??
            []),
        ]}
      />
      <When condition={isEstimated}>
        <Text
          size="sm"
          mb="0"
          mt={theme.spacing.spacing02}
          color={theme.color.text.text04}
        >
          *This KPI Value has been estimated based on the previous days&apos;
          data
        </Text>
      </When>
    </>
  );
}

type TimePointDetailsTableProps = { values: KeyValueWithColor[] };

export function TimePointDetailsTable({ values }: TimePointDetailsTableProps) {
  return (
    <table css={{ marginTop: theme.spacing.spacing01 }}>
      <tbody>
        {values.map(({ key, value, color }, index) => (
          <tr key={`${key}${value}${index}`}>
            <td>
              <Text
                mr={theme.spacing.spacing01}
                color={theme.color.text.text04}
                size="sm"
                as="span"
              >
                {key}:
              </Text>
            </td>
            <td>
              <Text
                color={color ?? theme.color.text.text04}
                as="span"
                size="sm"
              >
                {value}
              </Text>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
